import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, Activity, Edit2 } from 'lucide-react';
import toast from 'react-hot-toast';

export const MyDashboardTab = () => {
  const { currentUser, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    height: currentUser?.height || '',
    weight: currentUser?.weight || '',
    handedness: currentUser?.handedness || '',
    racketModel: currentUser?.racketModel || ''
  });
  
  // Calculate age from birthDate
  const birthDateStr = currentUser?.birthDate || (currentUser as any)?.customerProfile?.birthDate;
  let ageStr = 'Não informada';
  if (birthDateStr) {
    const bd = new Date(birthDateStr);
    if (!isNaN(bd.getTime())) {
      const diff = Date.now() - bd.getTime();
      const age = new Date(diff).getUTCFullYear() - 1970;
      ageStr = `${age} anos`;
    }
  }

  const [performance, setPerformance] = useState<{trMatches: any[], compMatches: any[]}>({ trMatches: [], compMatches: [] });
  const [loadingPerf, setLoadingPerf] = useState(true);
  
  const [filterType, setFilterType] = useState('ALL'); // ALL, simples, duplas
  const [filterSource, setFilterSource] = useState('ALL'); // ALL, RANKING, TOURNAMENT

  useEffect(() => {
    const fetchPerf = async () => {
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL || "https://techtennis-api.vercel.app"}/api/users/performance`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('tt_auth_token')}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPerformance(data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingPerf(false);
      }
    };
    fetchPerf();
  }, []);

  const handleSaveProfile = async () => {
    try {
      const success = await updateProfile({
        height: profileData.height ? Number(profileData.height) : undefined,
        weight: profileData.weight ? Number(profileData.weight) : undefined,
        handedness: profileData.handedness,
        racketModel: profileData.racketModel
      });
      if (success) {
        toast.success('Perfil atualizado!');
        setIsEditing(false);
      } else {
        toast.error('Erro ao atualizar perfil.');
      }
    } catch (e) {
      toast.error('Erro ao salvar');
    }
  };

  const allMatches = [...performance.trMatches, ...performance.compMatches].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  
  const filteredMatches = allMatches.filter(m => {
    if (filterType !== 'ALL') {
      const isDoubles = m.match_type?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes('dupla');
      if (filterType === 'SIMPLES' && isDoubles) return false;
      if (filterType === 'DUPLAS' && !isDoubles) return false;
    }
    if (filterSource !== 'ALL') {
      if (m.source !== filterSource) return false;
    }
    return true;
  }).slice(0, 10);

  const wins = filteredMatches.filter(m => m.winner_id === currentUser?.id).length;
  const losses = filteredMatches.length - wins;
  const winRate = filteredMatches.length > 0 ? Math.round((wins / filteredMatches.length) * 100) : 0;

  return (
    <div style={{ color: 'white', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{ background: 'rgba(255,255,255,0.05)', padding: '24px', borderRadius: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}><User size={20} /> Meu Perfil</h2>
          {!isEditing ? (
            <button onClick={() => setIsEditing(true)} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Edit2 size={14} /> Editar
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setIsEditing(false)} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer' }}>Cancelar</button>
              <button onClick={handleSaveProfile} style={{ background: 'var(--primary-color)', border: 'none', color: 'white', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Salvar</button>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Idade</label>
            <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '14px' }}>{ageStr}</div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Altura (m)</label>
            {isEditing ? (
              <input type="number" step="0.01" value={profileData.height} onChange={e => setProfileData({...profileData, height: e.target.value})} style={{ width: '100%', padding: '10px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white' }} />
            ) : (
              <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '14px' }}>{profileData.height || '-'}</div>
            )}
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Peso (kg)</label>
            {isEditing ? (
              <input type="number" step="0.1" value={profileData.weight} onChange={e => setProfileData({...profileData, weight: e.target.value})} style={{ width: '100%', padding: '10px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white' }} />
            ) : (
              <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '14px' }}>{profileData.weight || '-'}</div>
            )}
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Lateralidade</label>
            {isEditing ? (
              <select value={profileData.handedness} onChange={e => setProfileData({...profileData, handedness: e.target.value})} style={{ width: '100%', padding: '10px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white' }}>
                <option value="">Selecione</option>
                <option value="Destro">Destro</option>
                <option value="Canhoto">Canhoto</option>
                <option value="Ambidestro">Ambidestro</option>
              </select>
            ) : (
              <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '14px' }}>{profileData.handedness || '-'}</div>
            )}
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>Modelo da Raquete</label>
            {isEditing ? (
              <input type="text" value={profileData.racketModel} onChange={e => setProfileData({...profileData, racketModel: e.target.value})} style={{ width: '100%', padding: '10px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white' }} placeholder="Ex: Wilson Pro Staff 97" />
            ) : (
              <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '14px' }}>{profileData.racketModel || '-'}</div>
            )}
          </div>
        </div>
      </div>

      <div style={{ background: 'rgba(255,255,255,0.05)', padding: '24px', borderRadius: '12px' }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}><Activity size={20} /> Desempenho (Últimos {filteredMatches.length} Jogos)</h2>
        
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white' }}>
            <option value="ALL">Simples + Duplas</option>
            <option value="SIMPLES">Apenas Simples</option>
            <option value="DUPLAS">Apenas Duplas</option>
          </select>
          <select value={filterSource} onChange={e => setFilterSource(e.target.value)} style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white' }}>
            <option value="ALL">Ranking + Torneios</option>
            <option value="RANKING">Apenas Ranking</option>
            <option value="TOURNAMENT">Apenas Torneios</option>
          </select>
        </div>

        {loadingPerf ? (
          <p style={{ color: 'rgba(255,255,255,0.6)' }}>Carregando estatísticas...</p>
        ) : (
          <div>
            <div style={{ display: 'flex', gap: '24px', marginBottom: '32px' }}>
              <div style={{ flex: 1, background: 'rgba(0,0,0,0.2)', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '36px', fontWeight: 'bold', color: 'var(--primary-color)' }}>{winRate}%</div>
                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}>Taxa de Vitória</div>
              </div>
              <div style={{ flex: 1, background: 'rgba(0,0,0,0.2)', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#4CAF50' }}>{wins}</div>
                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}>Vitórias</div>
              </div>
              <div style={{ flex: 1, background: 'rgba(0,0,0,0.2)', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#F44336' }}>{losses}</div>
                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}>Derrotas</div>
              </div>
            </div>

            <h3 style={{ fontSize: '16px', marginBottom: '16px', color: 'rgba(255,255,255,0.8)' }}>Histórico Recente</h3>
            {filteredMatches.length === 0 ? (
              <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: '20px' }}>Nenhum jogo encontrado com os filtros atuais.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {filteredMatches.map(m => {
                  const isWin = m.winner_id === currentUser?.id;
                  const isPending = m.status === 'PENDING' || m.status === 'pending';
                  return (
                    <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 'bold', color: isPending ? '#FFC107' : (isWin ? '#4CAF50' : '#F44336') }}>
                          {isPending ? 'Pendente' : (isWin ? 'Vitória' : 'Derrota')}
                        </div>
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginTop: '4px' }}>
                          {m.comp_name || (m.source === 'RANKING' ? 'Ranking' : 'Torneio')}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        {isPending ? (
                          <button
                            onClick={() => navigate(`/competitions/${m.comp_id}?tab=matches`)}
                            style={{ padding: '6px 12px', background: 'var(--primary-color)', border: 'none', color: '#000', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}
                          >
                            Lançar Resultado
                          </button>
                        ) : (
                          <>
                            <div style={{ fontWeight: 'bold' }}>{m.score || '-'}</div>
                            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginTop: '4px' }}>
                              {new Date(m.date).toLocaleDateString('pt-BR')}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
